'use client'

import { useLayoutEffect, useRef } from 'react'

import { useIsMobileDevice } from '@/hooks/use-screen'
import { readVisualViewport } from '@/hooks/use-visual-viewport'

type FitMode = 'side' | 'bottom' | 'center'

// Inline properties a fit may write; cleared again on every update and on unmount.
const FIT_PROPERTIES = [
  'top',
  'bottom',
  'height',
  'max-height',
  'min-height',
  'overflow-y',
] as const

/**
 * Keeps a `position: fixed` modal surface (Sheet / Dialog content) inside the part of the screen
 * the on-screen keyboard leaves visible. iOS Safari never resizes the layout viewport for the
 * keyboard, so `inset-y-0` / `top-1/2` / `bottom-0` surfaces end up half behind it — the field
 * being typed into, or the submit button, hidden. Rendered as a hidden child of the surface and
 * writes to its parent directly, so it only listens while the surface is open and never
 * re-renders it. Touch devices only; desktop keeps the stylesheet's layout untouched.
 *
 * - `side`: a full-height left/right sheet — the visible viewport's top and height while the
 *   keyboard is up.
 * - `bottom`: a bottom sheet — sits on the keyboard, at most the visible height.
 * - `center`: a centred dialog — centred in the visible area and capped (scrolling inside) to
 *   it while the keyboard is up.
 */
export function VisualViewportFit({ mode }: { mode: FitMode }) {
  const markerRef = useRef<HTMLSpanElement>(null)
  const isMobileDevice = useIsMobileDevice()

  useLayoutEffect(() => {
    const surface = markerRef.current?.parentElement
    const viewport = window.visualViewport
    if (!isMobileDevice || !surface || !viewport) return

    const clear = () => FIT_PROPERTIES.forEach((p) => surface.style.removeProperty(p))
    const update = () => {
      const rect = readVisualViewport(viewport)
      clear()
      if (mode === 'side') {
        // Keyboard down, the stylesheet's own top/bottom are right (incl. room left for the
        // status bar); keyboard up, that bar is behind it, so take the whole visible area.
        if (!rect.keyboardOpen) return
        surface.style.top = `${rect.offsetTop}px`
        surface.style.bottom = 'auto'
        surface.style.height = `${rect.height}px`
      } else if (mode === 'bottom') {
        surface.style.bottom = `${Math.max(0, rect.layoutHeight - rect.offsetTop - rect.height)}px`
        surface.style.maxHeight = `${rect.height}px`
        surface.style.overflowY = 'auto'
      } else if (rect.keyboardOpen) {
        // translate(-50%, -50%) stays on, so `top` is the visible area's centre.
        surface.style.top = `${rect.offsetTop + rect.height / 2}px`
        surface.style.maxHeight = `${rect.height - 16}px`
        surface.style.minHeight = '0'
        surface.style.overflowY = 'auto'
      }
    }
    update()

    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
      clear()
    }
  }, [isMobileDevice, mode])

  return <span ref={markerRef} hidden />
}
